import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  contributionModerationApi,
  CONTRIBUTION_QUERY_KEYS,
} from '@api/endpoints/contributions'
import type { ContributionKind, ContributionStatus } from '@api/types/contributions'

interface QueueParams {
  status?: ContributionStatus
  kind?: ContributionKind
  skip?: number
  take?: number
}

export function useContributionQueue(params: QueueParams = {}) {
  return useQuery({
    queryKey: CONTRIBUTION_QUERY_KEYS.queue(params),
    queryFn: () => contributionModerationApi.getQueue(params),
    // Short, because this is a shared work queue: two moderators looking at the same list should
    // not spend long disagreeing about what is in it.
    staleTime: 30 * 1000,
  })
}

export function usePendingContributionCount() {
  return useQuery({
    queryKey: CONTRIBUTION_QUERY_KEYS.pendingCount,
    queryFn: () => contributionModerationApi.getPendingCount(),
    staleTime: 60 * 1000,
  })
}

/**
 * Accepting and refusing.
 *
 * Both invalidate the whole contributions key rather than one list: a decision moves a row out of
 * the pending queue and into a decided one, so every list that could be on screen is now wrong.
 */
export function useDecideContribution() {
  const queryClient = useQueryClient()
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: CONTRIBUTION_QUERY_KEYS.all })

  const approve = useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      contributionModerationApi.approve(id, note),
    onSuccess: invalidate,
  })

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      contributionModerationApi.reject(id, reason),
    onSuccess: invalidate,
  })

  return { approve, reject }
}
